import type { Schema } from '@/shared/api/contract';

/**
 * Straight from the contract: cubas.domain.schemas.client.
 *
 * The natural key is `code`, the ERP's own customer code, **not the NIF**: one company
 * can hold several cards in the ERP and the NIF repeats between them (contexto §4.7).
 * A client typed by hand has no code until the ERP is integrated.
 */
export type Client = Schema<'ClientRead'>;
export type ClientCreate = Schema<'ClientCreate'>;
export type ClientUpdate = Schema<'ClientUpdate'>;
export type ClientLookupRead = Schema<'ClientLookupRead'>;

/** Where a client row came from, in words. */
export const CLIENT_ORIGIN_LABELS: Record<string, string> = {
  manual: 'Alta manual',
  erp_api: 'Profer',
  erp_export: 'Profer (fichero)',
};

/** The address as the quote prints it: street, then postal code and city, then province. */
export function addressLines(client: Client): string[] {
  const locality = [client.postal_code, client.city].filter(Boolean).join(' ');
  return [client.street, locality, client.province].filter((line): line is string =>
    Boolean(line && line.trim()),
  );
}

/** What identifies this client at a glance: the ERP code, or the NIF, or nothing. */
export function clientSubtitle(client: Client): string {
  return [client.code, client.nif].filter(Boolean).join(' · ') || 'Sin código ni NIF';
}

/**
 * The shop's name when it says something the name does not (contract v10.46): «Talleres
 * San José» under «TALLERES SAN JOSE, S.L.» is the same words, «Brico Miengo» under
 * «Suministros Pas, S.A.» is not.
 */
export function tradeNameOf(client: Client): string | null {
  const trade = client.trade_name?.trim();
  if (!trade) return null;
  const simple = (value: string) =>
    value
      .toLowerCase()
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .replace(/[^a-z0-9]+/g, ' ')
      .trim();
  return simple(client.name).startsWith(simple(trade)) ? null : trade;
}
